import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { TextField, Tooltip, InputAdornment } from "@mui/material";
import AIAssistantLogo from "../../assets/Images/AIAssistantLogo1.png";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SearchIcon from "@mui/icons-material/Search";
import ChatMessage from "./ChatMessage";
import { isSameDay } from "date-fns";
import instance from "../../redux/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// --- Styles ---
const buttonWrapper = {
  position: "fixed",
  bottom: "60px",
  right: "30px",
  zIndex: 10000,
};

const contractedStyle = {
  width: "60px",
  height: "60px",
  borderRadius: "50%",
  background: "linear-gradient(135deg, #1672DE 0%, #1565c0 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow:
    "0px 8px 24px rgba(22, 114, 222, 0.35), 0px 4px 12px rgba(22, 114, 222, 0.2)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  border: "3px solid rgba(255, 255, 255, 0.9)",
  position: "relative",
  overflow: "hidden",
};

const contractedStyleHover = {
  transform: "scale(1.08)",
  boxShadow:
    "0px 12px 32px rgba(22, 114, 222, 0.45), 0px 6px 16px rgba(22, 114, 222, 0.25)",
};

const logoStyle = {
  width: "44px",
  height: "44px",
  borderRadius: "50%",
  objectFit: "cover",
  filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))",
  backgroundColor: "rgba(22, 114, 222, 0.1)",
};

const chatContainerStyle = {
  display: "flex",
  flexDirection: "column",
  height: "80vh",
  width: "30vw",
  minWidth: "380px",
  maxWidth: "90vw",
  backgroundColor: "#ffffff",
  backdropFilter: "blur(30px)",
  border: "1px solid rgba(22, 114, 222, 0.12)",
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow:
    "0 24px 64px rgba(0, 0, 0, 0.15), 0 8px 24px rgba(22, 114, 222, 0.1), 0 0 0 1px rgba(22, 114, 222, 0.05)",
  resize: "both",
  position: "fixed",
  zIndex: 10000,
  //transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  //   transition: "all 0.3s ease-in-out",
};

const headerStyle = {
  height: "64px",
  background: "linear-gradient(135deg, #1672DE 0%, #1565c0 100%)",
  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 20px",
  fontWeight: "600",
  color: "#ffffff",
  cursor: "move",
  fontSize: "16px",
  letterSpacing: "0.2px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  position: "relative",
  zIndex: 10,
};

const chatMessagesStyle = {
  flex: 1,
  overflowY: "auto",
  padding: "20px 16px",
  backgroundColor: "#f8f9fa",
  gap: "4px",
  display: "flex",
  flexDirection: "column",
  backgroundImage:
    "radial-gradient(circle at 1px 1px, rgba(22, 114, 222, 0.03) 1px, transparent 0)",
  backgroundSize: "20px 20px",
};

const footerStyle = {
  borderTop: "1px solid rgba(0, 0, 0, 0.08)",
  padding: "12px 16px",
  backgroundColor: "#ffffff",
  boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.04)",
};

// const inputStyle = {

//   "& .MuiInputBase-root": { backgroundColor: "#fff", borderRadius: "10px" , fontSize: "16px",},
//   "& .MuiOutlinedInput-root": {
//     "& fieldset": { border: "none" },
//     "&:hover fieldset": { border: "none" },
//     "&.Mui-focused fieldset": { border: "none" },
//   },
// };
const inputStyle = {
  "& .MuiInputBase-root": {
    backgroundColor: "#f8f9fa",
    borderRadius: "16px",
    fontSize: "15px",
    fontFamily: "inherit",
    transition: "all 0.3s ease",
    color: "#1a1a1a",
    padding: "8px 4px",
    "&:hover": {
      backgroundColor: "#f8f9fa",
    },
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "1.5px solid rgba(22, 114, 222, 0.15)",
      borderRadius: "16px",
    },
    "&:hover": {
      backgroundColor: "#f8f9fa",
      "& fieldset": {
        border: "1.5px solid rgba(22, 114, 222, 0.25)",
      },
    },
    "&.Mui-focused": {
      backgroundColor: "#ffffff",
      "& fieldset": {
        border: "2px solid #1672DE",
        boxShadow: "0 0 0 3px rgba(22, 114, 222, 0.1)",
      },
    },
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: "#9ca3af",
    opacity: 0.7,
    fontSize: "15px",
  },
};

const dateSeparatorStyle = {
  textAlign: "center",
  color: "#6b7280",
  margin: "16px 0",
  fontSize: "12px",
  fontWeight: "500",
  letterSpacing: "0.3px",
  position: "relative",
};

const highlightStyle = {
  backgroundColor: "#fef3c7",
  color: "#92400e",
  fontWeight: "600",
  borderRadius: "4px",
  padding: "2px 4px",
  boxShadow: "0 1px 2px rgba(146, 64, 14, 0.2)",
};

// --- Component ---
export default function FloatingAIAssistant() {
  const { userData } = useSelector((state) => state?.user);
  const [value, setValue] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(false);

  const messagesEndRef = useRef(null);
  const headerRef = useRef(null);
  const offset = useRef({ x: 0, y: 0 });
  const fileInputRef = useRef(null);
  const chatBoxRef = useRef(null);

  useEffect(() => {
    if (expanded) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [expanded]);

  // useEffect(() => {
  //   const fetchHistory = async () => {
  //     if (expanded && userData?.id) {
  //       setLoadingHistory(true); // start loader
  //       try {
  //         const resp = await instance.get(`documents/history/${userData.id}/`);
  //         if (resp?.data?.history) {
  //           const merged = resp.data.history.flatMap((h) => {
  //             const userMsg = {
  //               text: h.question,
  //               isSender: true,
  //               timestamp: h.timestamp,
  //               formattedTime: new Date(h.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
  //             };
  //             const botMsg = {
  //               text: h.answer,
  //               isSender: false,
  //               timestamp: h.timestamp,
  //               formattedTime: new Date(h.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
  //             };
  //             return [userMsg, botMsg];
  //           });
  //           setMessages(merged);
  //         }
  //       } catch (err) {
  //         console.error("Failed to fetch history:", err);
  //       } finally {
  //         setLoadingHistory(false);
  //       }
  //     }
  //   };
  //   fetchHistory();
  // }, [expanded, userData?.id]);

  useEffect(() => {
    const fetchHistory = async () => {
      if (expanded && userData?.id) {
        setLoadingHistory(true); // start loader
        try {
          const resp = await instance.get(`documents/history/${userData.id}/`);
          if (resp?.data?.history) {
            // --- START FIX ---
            const merged = resp.data.history.flatMap((h) => {
              // Check and convert the bot's answer
              let botAnswer = h.answer;
              if (typeof botAnswer === "object" && botAnswer !== null) {
                botAnswer = JSON.stringify(botAnswer, null, 2);
              }

              const userMsg = {
                text: h.question,
                isSender: true,
                timestamp: h.timestamp,
                formattedTime: new Date(h.timestamp).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                }),
              };
              const botMsg = {
                text: String(botAnswer), // Use the converted string
                isSender: false,
                timestamp: h.timestamp,
                formattedTime: new Date(h.timestamp).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                }),
              };
              return [userMsg, botMsg];
            });
            // --- END FIX ---

            setMessages(merged);
          }
        } catch (err) {
          console.error("Failed to fetch history:", err);
        } finally {
          setLoadingHistory(false);
        }
      }
    };
    fetchHistory();
  }, [expanded, userData?.id]);

  useEffect(() => {
    if (expanded && !loadingHistory && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [loadingHistory, expanded, messages]);

  // scroll only when YOU send a message
  useEffect(() => {
    if (expanded && messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.isSender) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [messages, expanded]);

  // --- Dragging Logic (fixed) ---
  const handleMouseDown = (e) => {
    if (!chatBoxRef.current) return;
    e.preventDefault(); // prevent text selection while dragging

    const rect = chatBoxRef.current.getBoundingClientRect();
    offset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    // ensure it's fixed positioned so left/top changes take effect
    chatBoxRef.current.style.position = "fixed";
    chatBoxRef.current.style.zIndex = 10000;

    // prevent text selection while dragging
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    if (headerRef.current) headerRef.current.style.cursor = "grabbing";

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!chatBoxRef.current) return;

    const left = e.clientX - offset.current.x;
    const top = e.clientY - offset.current.y;

    // constrain inside viewport
    const maxLeft = Math.max(
      0,
      window.innerWidth - chatBoxRef.current.offsetWidth
    );
    const maxTop = Math.max(
      0,
      window.innerHeight - chatBoxRef.current.offsetHeight
    );

    chatBoxRef.current.style.left = `${Math.min(Math.max(0, left), maxLeft)}px`;
    chatBoxRef.current.style.top = `${Math.min(Math.max(0, top), maxTop)}px`;
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);

    // restore selection / cursor
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    if (headerRef.current) headerRef.current.style.cursor = "move";
  };

  // --- Message Handling ---
  const appendMessage = (text, sender = true) => {
    const timestamp = new Date();
    const newMsg = {
      text,
      isSender: sender,
      timestamp: timestamp.toISOString(),
      formattedTime: timestamp.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
    setMessages((prev) => [...prev, newMsg]);
  };

  const formatMessage = () => {
    const formattedMsg = value.trim();
    if (formattedMsg) {
      appendMessage(formattedMsg, true);
      sendMessage(formattedMsg);
      setValue("");
    }
  };

  const sendMessage = async (text) => {
    setIsTyping(true);
    const apiPayload = {
      user_id: userData?.id,
      queryMessage: { text },
    };
    try {
      const resp = await instance.post("documents/cmtaichat/", apiPayload);
      setIsTyping(false);

      let botResponse = resp?.data?.response;

      // Check if the response is an object/array and not null
      if (typeof botResponse === "object" && botResponse !== null) {
        // Convert it to a formatted JSON string
        botResponse = JSON.stringify(botResponse, null, 2);
      } else if (!botResponse) {
        // Handle empty/null responses
        botResponse = "No response";
      }

      // Now, botResponse is guaranteed to be a string
      appendMessage(String(botResponse), false);
    } catch (error) {
      console.error("Chat API Error:", error);
      setIsTyping(false);
      appendMessage("Sorry, an error occurred.", false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) uploadFile(file);
    e.target.value = null;
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("files", file);

    try {
      setIsUploading(true);
      const resp = await instance.post("documents/upload/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (resp.status === 201) {
        // Success response from backend
        setIsUploading(false);
        appendMessage(`File "${file.name}" uploaded successfully.`, false); // bot message
        toast.success(`Uploaded: ${file.name}`);
      } else {
        // Just in case backend returns some unexpected status
        appendMessage(`Failed to upload "${file.name}".`, false);
        toast.error(`❌ Failed to upload "${file.name}"`);
      }
    } catch (error) {
      console.error("File upload failed:", error);
      appendMessage(`Failed to upload "${file.name}".`, false);
      toast.error(`❌ Upload failed for "${file.name}"`);
    }
  };

  // --- Search Highlight ---
  const highlightText = (text) => {
    if (!searchQuery) return text;
    const regex = new RegExp(`(${searchQuery})`, "gi");
    return text.split(regex).map((part, i) =>
      regex.test(part) ? (
        <span key={i} style={highlightStyle}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <>
      {!expanded && (
        <div style={buttonWrapper}>
          <div
            style={contractedStyle}
            onClick={() => setExpanded(true)}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, contractedStyleHover);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = contractedStyle.boxShadow;
            }}
          >
            <img
              src={AIAssistantLogo}
              alt="ai_assistant_logo"
              style={logoStyle}
            />
          </div>
        </div>
      )}

      {expanded && (
        <div
          ref={chatBoxRef}
          style={{
            ...chatContainerStyle,
            right: "30px",
            bottom: "100px",
            opacity: expanded ? 1 : 0,
            transform: expanded ? "scale(1)" : "scale(0.9)",
          }}
        >
          {/* Header */}
          <div
            ref={headerRef}
            style={headerStyle}
            onMouseDown={handleMouseDown}
          >
            {!searchMode && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.95)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    border: "2px solid rgba(255, 255, 255, 0.8)",
                  }}
                >
                  <img
                    src={AIAssistantLogo}
                    alt="ai_assistant_logo"
                    style={{
                      width: "22px",
                      height: "22px",
                      objectFit: "cover",
                    }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ fontWeight: "600", fontSize: "16px", color: "#ffffff" }}>
                    CMT AI Assistant
                  </span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginTop: "2px",
                    }}
                  >
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: "#00ff4c",
                        boxShadow: "0 0 6px 2px rgba(0, 255, 76, 0.7)",
                      }}
                    ></span>
                    <span
                      style={{
                        fontSize: "13px",
                        color: "#ffffff",
                        fontWeight: "500",
                      }}
                    >
                      Online
                    </span>
                  </div>
                </div>
              </div>
            )}
            {searchMode && (
              <TextField
                autoFocus
                size="small"
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  background: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "12px",
                  flex: 1,
                  marginRight: "12px",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    },
                    "&:hover fieldset": {
                      border: "1px solid rgba(255, 255, 255, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      border: "2px solid rgba(255, 255, 255, 0.8)",
                    },
                  },
                }}
              />
            )}
            <div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {!searchMode ? (
                  <Tooltip title="Search messages" arrow>
                    <SearchIcon
                      sx={{
                        cursor: "pointer",
                        color: "rgba(255, 255, 255, 0.9)",
                        fontSize: "22px",
                        transition: "all 0.2s ease",
                        padding: "4px",
                        borderRadius: "8px",
                        "&:hover": {
                          transform: "scale(1.1)",
                          backgroundColor: "rgba(255, 255, 255, 0.2)",
                          color: "#ffffff",
                        },
                      }}
                      onClick={() => setSearchMode(true)}
                    />
                  </Tooltip>
                ) : (
                  <CloseIcon
                    sx={{
                      cursor: "pointer",
                      color: "rgba(255, 255, 255, 0.9)",
                      fontSize: "22px",
                      transition: "all 0.2s ease",
                      padding: "4px",
                      borderRadius: "8px",
                      "&:hover": {
                        transform: "rotate(90deg)",
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        color: "#ffffff",
                      },
                    }}
                    onClick={() => {
                      setSearchMode(false);
                      setSearchQuery("");
                    }}
                  />
                )}
                <Tooltip title="Minimize" arrow>
                  <RemoveIcon
                    sx={{
                      cursor: "pointer",
                      color: "rgba(255, 255, 255, 0.9)",
                      fontSize: "22px",
                      transition: "all 0.2s ease",
                      padding: "4px",
                      borderRadius: "8px",
                      "&:hover": {
                        transform: "scale(1.1)",
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        color: "#ffffff",
                      },
                    }}
                    onClick={() => setExpanded(false)}
                  />
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div style={chatMessagesStyle}>
            {loadingHistory ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "20px",
                  gap: "10px",
                }}
              >
                <div className="loader"></div>
                <div style={{ color: "#666", fontSize: "14px" }}>
                  Loading history...
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, index) => {
                  const msgDate = new Date(msg.timestamp);
                  const showDate =
                    index === 0 ||
                    !isSameDay(
                      msgDate,
                      new Date(messages[index - 1].timestamp)
                    );
                  return (
                    <div key={index}>
                      {showDate && (
                        <div
                          style={dateSeparatorStyle}
                          className="date-separator"
                        >
                          <span
                            style={{
                              backgroundColor: "#f8f9fa",
                              padding: "4px 12px",
                              borderRadius: "12px",
                              display: "inline-block",
                            }}
                          >
                            {msgDate.toLocaleDateString([], {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        </div>
                      )}
                      <ChatMessage
                        message={{
                          ...msg,
                          text: searchQuery
                            ? highlightText(msg.text)
                            : msg.text,
                        }}
                        isSender={msg.isSender}
                      />
                    </div>
                  );
                })}
                {isTyping && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "12px 16px",
                      backgroundColor: "#e3f2fd",
                      borderRadius: "18px",
                      maxWidth: "85%",
                      borderTopLeftRadius: "4px",
                      boxShadow: "0 2px 8px rgba(22, 114, 222, 0.1)",
                      border: "1px solid rgba(22, 114, 222, 0.12)",
                      fontStyle: "italic",
                      color: "#6b7280",
                      fontSize: "14px",
                    }}
                  >
                    <span>Bot is typing</span>
                    <span className="dot-typing">...</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Footer */}
          <div style={footerStyle}>
            <TextField
              multiline
              fullWidth
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  formatMessage();
                }
              }}
              placeholder="Type your message..."
              rows={2}
              sx={inputStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Attach file" arrow>
                      <AttachFileIcon
                        sx={{
                          cursor: "pointer",
                          marginRight: "12px",
                          color: "#6b7280",
                          fontSize: "22px",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            color: "#1672DE",
                            transform: "scale(1.1) rotate(15deg)",
                          },
                        }}
                        onClick={() => fileInputRef.current.click()}
                      />
                    </Tooltip>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                    <Tooltip title="Send message" arrow>
                      <SendIcon
                        sx={{
                          cursor: "pointer",
                          color: "#1672DE",
                          fontSize: "22px",
                          transition: "all 0.2s ease",
                          padding: "4px",
                          borderRadius: "50%",
                          "&:hover": {
                            backgroundColor: "rgba(22, 114, 222, 0.1)",
                            transform: "scale(1.1)",
                            boxShadow: "0 2px 8px rgba(22, 114, 222, 0.2)",
                          },
                        }}
                        onClick={formatMessage}
                      />
                    </Tooltip>
                    {isUploading && (
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "12px",
                          color: "gray",
                        }}
                      >
                        Uploading...
                      </span>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>
      )}

      {/* Typing dots animation CSS */}
      <style>
        {`
        .dot-typing::after {
          content: '';
          animation: dots 1.2s steps(4, end) infinite;
        }
        @keyframes dots {
          0%, 25% { content: ''; }
          50% { content: '.'; }
          75% { content: '..'; }
          100% { content: '...'; }
        }
      `}
        {`
.loader {
  width: 48px;
  aspect-ratio: 1;
  margin: 0 auto;
  border-radius: 50%;
  background: 
    radial-gradient(farthest-side, #1672DE 94%, #0000) top/6px 6px no-repeat,
    conic-gradient(#0000 30%, #1672DE);
  -webkit-mask: radial-gradient(farthest-side, #0000 calc(100% - 6px), #000 0);
  animation: l13 0.8s infinite linear;
}
@keyframes l13{ 
  100%{transform: rotate(1turn)}
}
`}
        {`
/* Custom scrollbar styling */
div[style*="overflowY: auto"]::-webkit-scrollbar {
  width: 8px;
}
div[style*="overflowY: auto"]::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}
div[style*="overflowY: auto"]::-webkit-scrollbar-thumb {
  background: rgba(22, 114, 222, 0.2);
  border-radius: 10px;
  transition: background 0.2s ease;
}
div[style*="overflowY: auto"]::-webkit-scrollbar-thumb:hover {
  background: rgba(22, 114, 222, 0.35);
}
.date-separator {
  position: relative;
}
.date-separator::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  background: linear-gradient(to right, transparent, rgba(107, 114, 128, 0.15), transparent);
  z-index: 0;
}
.date-separator span {
  position: relative;
  z-index: 1;
}
`}
      </style>
    </>
  );
}
